/**
 * AttachmentComposer — paperclip + selected-file previews for chat/ticket/note UIs.
 *
 * Props:
 *   files:   array of { id, url, thumb_url, content_type, size, original_filename }
 *            currently attached (uploaded already).
 *   onChange: (nextFiles) => void — parent stores the array.
 *   maxFileMb: default 50
 *   compact:  smaller UI when true (for chat input)
 *
 * Uploads happen immediately when the user picks a file — the parent only
 * receives already-persisted file records with a valid `id`. This decouples
 * the composer from the message-submit flow.
 */
import React, { useCallback, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

// Extensions blocked client-side too — matches backend BLOCKED_EXTS.
const BLOCKED_EXTS = [
  "exe","bat","cmd","com","scr","pif","msi","vbs","vbe","wsf","wsh",
  "jar","app","apk","ipa","sh","bash","ps1","psm1","reg","dll","lnk",
  "run","bin","js","jse","cpl","action",
];

const MB = 1024 * 1024;

function fmtSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / MB).toFixed(1)} MB`;
}

export default function AttachmentComposer({ files = [], onChange, maxFileMb = 50, compact = false, testId = "attachment" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const f of fileList) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (BLOCKED_EXTS.includes(ext)) {
        toast.error(`.${ext} files are not allowed`);
        continue;
      }
      if (f.size > maxFileMb * MB) {
        toast.error(`${f.name} is larger than ${maxFileMb} MB`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("purpose", "attachment");
        const { data } = await api.post("/files/upload?purpose=attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    if (uploaded.length) onChange([...(files || []), ...uploaded]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  }, [files, onChange, maxFileMb]);

  const removeFile = (id) => onChange((files || []).filter((f) => f.id !== id));

  return (
    <div className="space-y-2">
      {files && files.length > 0 && (
        <div className={`flex flex-wrap gap-2 ${compact ? "" : ""}`} data-testid={`${testId}-list`}>
          {files.map((f) => {
            const isImage = f.content_type?.startsWith("image/");
            return (
              <div
                key={f.id}
                className="group relative border border-border rounded-lg overflow-hidden bg-muted/30"
                style={{ width: isImage ? 120 : 200 }}
                data-testid={`${testId}-preview-${f.id}`}
              >
                {isImage ? (
                  <img src={f.thumb_url || f.url} alt={f.original_filename} className="w-30 h-24 object-cover" />
                ) : (
                  <div className="flex items-center gap-2 p-2.5">
                    <FileText className="w-6 h-6 text-primary shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs truncate">{f.original_filename}</div>
                      <div className="text-[10px] text-muted-foreground">{fmtSize(f.size)}</div>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(f.id)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-background/80 backdrop-blur border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`${testId}-remove-${f.id}`}
                  aria-label="Remove"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => upload(Array.from(e.target.files || []))}
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          variant="ghost"
          size={compact ? "sm" : "default"}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-btn`}
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}
          {uploading ? "Uploading…" : "Attach"}
        </Button>
      </div>
    </div>
  );
}

/**
 * AttachmentList — read-only render of already-attached files with inline
 * image previews and download links. Used inside message bubbles.
 */
export function AttachmentList({ files = [], testId = "attachment-list" }) {
  if (!files || files.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid={testId}>
      {files.map((f) => {
        const isImage = f.content_type?.startsWith("image/");
        return (
          <a
            key={f.id}
            href={f.url}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-2 border border-border rounded-lg overflow-hidden hover:border-primary/50 transition-colors bg-card"
            data-testid={`${testId}-item-${f.id}`}
            download={f.original_filename}
          >
            {isImage ? (
              <img src={f.thumb_url || f.url} alt={f.original_filename} className="w-24 h-24 object-cover" />
            ) : (
              <div className="flex items-center gap-2 p-2.5">
                <FileText className="w-5 h-5 text-primary" />
                <div className="min-w-0">
                  <div className="text-xs truncate max-w-[140px]">{f.original_filename}</div>
                  <div className="text-[10px] text-muted-foreground">{fmtSize(f.size)}</div>
                </div>
              </div>
            )}
          </a>
        );
      })}
    </div>
  );
}
